import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Loader2, Monitor, Search, Smartphone } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@meno/ui/components/ui/dialog'
import { Input } from '@meno/ui/components/ui/input'
import { Label } from '@meno/ui/components/ui/label'
import { Textarea } from '@meno/ui/components/ui/textarea'
import { TextInput } from '@meno/ui/components/shared/TextInput'

// One screen as it appears in the picker. We accept the loose `Screen`
// shape used elsewhere instead of binding to a specific page's type so
// this dialog can be reused by Dashboard, settings, etc.
export type GroupScreen = {
  id: number
  name: string
  status?: string
  orientation_type?: 'horizontal' | 'vertical'
  rotation_degrees?: 0 | 90 | 180 | 270
  location?: string
}

export type ScreenGroupDialogProps = {
  group: { id: number; name: string; description?: string; screens: { id: number }[] } | null
  allScreens: GroupScreen[]
  onSave: (data: { name: string; description: string; screen_ids: number[] }) => Promise<void>
  onClose: () => void
}

// ScreenGroupDialog — create / edit a named bundle of screens. We keep
// the picker simple (search + checkbox list) since groups can hold any
// subset of org screens and most orgs sit under a few dozen.
export default function ScreenGroupDialog({
  group, allScreens, onSave, onClose,
}: ScreenGroupDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(group?.name ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [selected, setSelected] = useState<Set<number>>(
    new Set(group?.screens.map(s => s.id) ?? []),
  )
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  // Reset when the dialog is reopened with a different `group`.
  useEffect(() => {
    setName(group?.name ?? '')
    setDescription(group?.description ?? '')
    setSelected(new Set(group?.screens.map(s => s.id) ?? []))
    setSearch('')
  }, [group])

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSave = async () => {
    if (!name.trim() || saving) return
    setSaving(true)
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        screen_ids: Array.from(selected),
      })
    } finally {
      setSaving(false)
    }
  }

  const q = search.trim().toLowerCase()
  const filtered = q
    ? allScreens.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.location?.toLowerCase().includes(q),
      )
    : allScreens

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="max-w-lg flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {group ? t('group.edit', 'Edit Group') : t('group.new', 'New Group')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          <TextInput
            label={t('group.name', 'Group name')}
            value={name}
            onChange={setName}
            required
            maxLength={150}
            lang="any"
            placeholder={t('group.placeholder', 'e.g. Riyadh Lobby Screens')}
            autoFocus
          />
          <div className="grid gap-1.5">
            <Label>{t('common.description', 'Description')}</Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder={t('common.optionalDescription', 'Optional description...')}
              className="resize-none"
            />
          </div>

          {/* Screen picker — search + checkbox-style rows. Empty allowed:
              you can create an empty group and add screens later. */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>{t('group.screens', 'Screens')}</Label>
              <span className="text-[11px] text-muted-foreground">
                {selected.size} {t('screens.selected', 'selected')}
              </span>
            </div>
            <div className="relative mb-2">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('common.search', 'Search')}
                className="ps-8 h-9 text-sm"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
              {allScreens.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t('group.noScreensYet', 'No screens registered yet.')}
                </p>
              )}
              {allScreens.length > 0 && filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">
                  {t('common.noData', 'No data found')}
                </p>
              )}
              {filtered.map(s => {
                const isSelected = selected.has(s.id)
                const isVertical = s.orientation_type === 'vertical'
                const Icon = isVertical ? Smartphone : Monitor
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggle(s.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-start transition-colors ${
                      isSelected ? 'bg-muted border border-primary/30' : 'hover:bg-muted border border-transparent'
                    }`}
                  >
                    <div className="w-10 h-7 rounded-md bg-muted flex items-center justify-center shrink-0 border border-border/40">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{s.name}</p>
                      {s.location && (
                        <p className="text-[10px] text-muted-foreground truncate">{s.location}</p>
                      )}
                    </div>
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                      s.status === 'online' ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                    }`} />
                    <div className={`w-4 h-4 rounded-full border-2 shrink-0 ${
                      isSelected ? 'bg-primary border-primary' : 'border-border'
                    }`} />
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={saving} onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button disabled={!name.trim() || saving} onClick={handleSave} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {group ? t('common.save', 'Save') : t('common.create', 'Create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
